import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import 'react-day-picker/dist/style.css';
import CalendarIcon from '../../assets/calendar.png';
import { DatePickerProps, Mode } from './datePicker.type';
import './datePicker.css';
import { getIdAttributes, maxDate, minDate } from '../../utils/helper';
import { DateRange } from 'react-day-picker';
import DateSelector from '../datePicker/dateSelector';

export const idPrefix: string = 'date-picker';

const DatePickerV2: FC<DatePickerProps> = (props) => {
  const isSingleMode: boolean = props.mode === Mode.Single;
  const prefix: string = idPrefix;

  // State for Date Logic
  const [appliedDate, setAppliedDate] = useState<DateRange | undefined>(
    props.finalRange,
  );
  const [tempSelectedDate, setTempSelectedDate] = useState<
    DateRange | undefined
  >(props.finalRange);
  const [month, setMonth] = useState<Date | null | undefined>(
    props.finalRange?.from,
  );
  const [wholeMonth, setWholeMonth] = useState<boolean>(false);
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);

  // State for Popper Refs (Use state instead of useRef to ensure updates on mount)
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );

  // Calculate Placement
  const placement =
    props.position === 'left'
      ? 'bottom-start'
      : props.position === 'right'
        ? 'bottom-end'
        : 'bottom';

  // Popper Hook
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      { name: 'offset', options: { offset: [0, 6] } }, // 6px gap
      { name: 'preventOverflow', options: { boundary: 'clippingParents' } }, // Prevents being cut off
    ],
  });

  // Handle Apply
  const handleDateApply = (selected: DateRange) => {
    setAppliedDate(selected);
    props.handleApply(selected);
    setShowDatePicker(false);
  };

  // Sync Props
  useEffect(() => {
    setMonth(props.finalRange?.from);
    const range: DateRange | undefined = props.finalRange;
    if (range?.from && !range.to) {
      setWholeMonth(false);
    }
  }, [props.finalRange]);

  useEffect(() => {
    setAppliedDate(props.finalRange);
  }, [props.finalRange]);

  // Handle Cancel
  const handleCancel = () => {
    setTempSelectedDate(appliedDate);
    setShowDatePicker(false);
  };

  // Handle Click Outside (Closes dropdown when clicking outside Container AND Dropdown)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!showDatePicker) return;
      const target = event.target as Node;

      // Check if click is inside the Container (reference) or the Dropdown (popper)
      const isInsideContainer = referenceElement?.contains(target);
      const isInsideDropdown = popperElement?.contains(target);

      if (!isInsideContainer && !isInsideDropdown) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker, referenceElement, popperElement]);

  // Display Text Logic
  const displayRange = isSingleMode
    ? appliedDate?.from
      ? moment(appliedDate.from).format('DD/MM/YYYY')
      : 'DD/MM/YYYY'
    : appliedDate?.from
      ? `${moment(appliedDate.from).format('DD/MM/YYYY')} - ${
          appliedDate.to ? moment(appliedDate.to).format('DD/MM/YYYY') : '...'
        }`
      : 'DD/MM/YYYY - DD/MM/YYYY';

  return (
    <div
      ref={setReferenceElement} // Ref attached here for Container positioning
      className="relative inline-block min-w-[200px] w-full"
      {...getIdAttributes(prefix, 'container')}
    >
      <button
        onClick={() => setShowDatePicker((prev) => !prev)}
        type="button"
        className="display-date-button-contain"
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          padding: '12px 16px',
          backgroundColor: '#FAFAFA',
          border: showDatePicker ? '1px solid #561bdc' : 'none',
        }}
        {...getIdAttributes(prefix, 'button')}
      >
        <span className="text-xs text-[#1F1F1F] font-helvetica text-center">
          {displayRange}
        </span>
        <img
          src={CalendarIcon}
          alt="Calendar Icon"
          className="w-4 h-4"
          {...getIdAttributes(prefix, 'icon')}
        />
      </button>

      {showDatePicker &&
        ReactDOM.createPortal(
          <div
            ref={setPopperElement}
            style={{
              ...styles.popper,
              zIndex: 9999,
              // Ensure it matches container width (referenceElement is the container)
              minWidth: referenceElement?.offsetWidth ?? undefined,
              // Hide until position is calculated to prevent top-left flash
              visibility: styles.popper.transform ? 'visible' : 'hidden',
            }}
            {...attributes.popper}
            {...getIdAttributes(prefix, 'selector-wrapper')}
          >
            <DateSelector
              className={props.className}
              selectedDate={tempSelectedDate}
              setSelectedDate={setTempSelectedDate}
              setShowDatePicker={setShowDatePicker}
              handleApply={handleDateApply}
              showDatePicker={showDatePicker}
              maxDate={maxDate}
              minDate={minDate}
              month={month}
              setMonth={setMonth}
              handleCancel={handleCancel}
              wholeMonth={wholeMonth}
              setWholeMonth={setWholeMonth}
              mode={props.mode}
              position={props.position}
              {...getIdAttributes(prefix, 'selector')}
            />
          </div>,
          document.body,
        )}
    </div>
  );
};

export default DatePickerV2;
